import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  EnvelopeSimple,
  Phone,
  WhatsappLogo,
  LinkedinLogo,
  Clock,
  MapPin,
  Translate,
  CalendarCheck,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
};

const WHATSAPP_URL = "https://wa.me/902128300378";
const LINKEDIN_URL = "https://www.linkedin.com/company/altin-link";
const MAP_EMBED =
  "https://www.google.com/maps?q=Venezia+Mall+Istanbul&output=embed";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent. We'll respond shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    {
      icon: MapPin,
      label: "Office",
      value: "Istanbul · Venezia Mall, J Block No: 01",
    },
    {
      icon: EnvelopeSimple,
      label: "Email",
      value: "info@altin-link.com",
      href: "mailto:info@altin-link.com",
    },
    {
      icon: Phone,
      label: "Telephone",
      value: "+90 212 830 03 78",
      href: "tel:+902128300378",
    },
    {
      icon: Phone,
      label: "Telephone",
      value: "+90 212 803 12 75",
      href: "tel:+902128031275",
    },
    {
      icon: Clock,
      label: "Business Hours",
      value: "Mon — Fri · 09:00 – 18:00 (GMT+3)",
    },
    {
      icon: Translate,
      label: "Languages Spoken",
      value: "Turkish · English · Arabic",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="overline">Get In Touch</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight">
              Let&apos;s build business{" "}
              <span className="italic text-altin-gold">opportunities</span>{" "}
              together.
            </h2>
            <p className="mt-6 text-altin-muted leading-relaxed max-w-md">
              Whether you are looking for reliable suppliers, strategic
              partners, investors or new markets, Altin Link is ready to
              support your business objectives.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[#1ebd58] transition-colors"
              >
                <WhatsappLogo size={18} weight="light" /> WhatsApp
              </a>
              <a
                href="#contact-form"
                data-testid="contact-schedule-btn"
                className="inline-flex items-center gap-2 bg-altin-ink text-white px-5 py-3 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors"
              >
                <CalendarCheck size={18} weight="light" /> Schedule Meeting
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-linkedin-btn"
                className="inline-flex items-center gap-2 border border-altin-gold text-altin-ink px-5 py-3 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold hover:text-white transition-colors"
              >
                <LinkedinLogo size={18} weight="light" /> LinkedIn
              </a>
            </div>

            <div className="mt-10 space-y-px border-y border-altin-border">
              {info.map((item) => {
                const Icon = item.icon;
                const inner = (
                  <div className="flex items-start gap-5 py-5">
                    <div className="w-10 h-10 grid place-items-center border border-altin-gold/40 text-altin-gold shrink-0">
                      <Icon size={18} weight="light" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-altin-muted">
                        {item.label}
                      </div>
                      <div className="text-altin-ink mt-1 group-hover:text-altin-gold transition-colors">
                        {item.value}
                      </div>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group block hover:bg-altin-bg/50 transition-colors px-2 -mx-2"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={item.label}
                    data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group px-2 -mx-2"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>

            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-3">
                Visit our office
              </div>
              <div
                className="relative w-full h-64 border border-altin-border overflow-hidden"
                data-testid="contact-map"
              >
                <iframe
                  title="Altin Link office — Venezia Mall, Istanbul"
                  src={MAP_EMBED}
                  className="w-full h-full grayscale-[0.4]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </motion.div>

          <motion.form
            id="contact-form"
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 bg-altin-bg p-8 md:p-12 self-start"
          >
            <div className="mb-8">
              <div className="overline mb-3">Schedule a Consultation</div>
              <h3 className="font-serif-display text-2xl text-altin-ink tracking-tight">
                Tell us about your business objectives.
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              <Field id="name" label="Full Name *" value={form.name} onChange={update("name")} />
              <Field id="email" label="Email Address *" type="email" value={form.email} onChange={update("email")} />
              <Field id="phone" label="Phone" value={form.phone} onChange={update("phone")} />
              <Field id="company" label="Company" value={form.company} onChange={update("company")} />
              <div className="sm:col-span-2">
                <Field id="subject" label="Subject" value={form.subject} onChange={update("subject")} />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink placeholder:text-altin-muted/60 transition-colors resize-none"
                  placeholder="Industry · region · objectives..."
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-xs text-altin-muted max-w-xs">
                By submitting, you agree to be contacted by Altin Link for
                business-related communication.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-altin-ink text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Message"}
                <PaperPlaneTilt
                  weight="light"
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", value, onChange }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={`contact-input-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink placeholder:text-altin-muted/60 transition-colors"
      />
    </div>
  );
}
